import {
  AdminGetThreadAnalysisResults200Response,
  AgentChatMessage,
  DefaultApi,
  ThreadAnalysisDefItem,
} from "@coeff/api";
import { useQuery } from "@tanstack/react-query";

import { useApiContext } from "../utils/contexts";

import { QueryCacheKey } from ".";

const fetchThreadAnalyses = async (api: DefaultApi): Promise<ThreadAnalysisDefItem[]> => {
  const response = await api.adminListThreadAnalyses();
  return response.data.analyses ?? [];
};

export const useThreadAnalyses = () => {
  const { apiClient: api } = useApiContext();

  return useQuery([QueryCacheKey.THREAD_ANALYSES], () => fetchThreadAnalyses(api));
};

export interface ThreadAnalysisResultsFilters {
  email?: string;
  domain?: string;
  agent_type?: string;
  /** ISO date/datetime, half-open window over analyzed_through_dt: since <= t < until. */
  since?: string;
  until?: string;
  limit?: number;
  offset?: number;
}

const fetchThreadAnalysisResults = async (
  api: DefaultApi,
  analysisId: string,
  filters: ThreadAnalysisResultsFilters
): Promise<AdminGetThreadAnalysisResults200Response> => {
  const response = await api.adminGetThreadAnalysisResults({
    analysis_id: analysisId,
    ...filters,
  });
  return response.data;
};

export const useThreadAnalysisResults = (
  analysisId: string | undefined,
  filters: ThreadAnalysisResultsFilters
) => {
  const { apiClient: api } = useApiContext();

  return useQuery(
    [QueryCacheKey.THREAD_ANALYSIS_RESULTS, analysisId, filters],
    () => fetchThreadAnalysisResults(api, analysisId as string, filters),
    { enabled: !!analysisId, keepPreviousData: true }
  );
};

const fetchAgentThreadMessages = async (
  api: DefaultApi,
  threadId: string
): Promise<AgentChatMessage[]> => {
  const response = await api.adminGetAgentThreadMessages({ thread_id: threadId });
  return response.data.messages ?? [];
};

export const useAgentThreadMessages = (threadId: string | undefined) => {
  const { apiClient: api } = useApiContext();

  return useQuery(
    [QueryCacheKey.AGENT_THREAD_MESSAGES, threadId],
    () => fetchAgentThreadMessages(api, threadId as string),
    { enabled: !!threadId }
  );
};
